import { useMemo } from "react";
import { type ClientInferResponseBody } from "@ts-rest/core";

import { type Currency } from "~/api/products/types";
import { type apiContract } from "~/api/contract";
import { type ReceiptReport, type SalesReport } from "~/hooks/queries/reports";

import {
  assembleReportSections,
  buildSalesSummary,
  fieldToCents,
  getFirstCurrency,
  sumCashRefunds
} from "./report-builders";
import {
  type CashDrawerData,
  type ReportSectionsData,
  type SalesSummaryData
} from "./sections/types";

type CombinedReportResponse = ClientInferResponseBody<
  typeof apiContract.v2.pos.reportsCombined.get,
  200
>;
export type ReceiptCombinedReport = CombinedReportResponse["receipt"];

// Derive the per-till shape from the API contract (not a hand-written type), so
// a backend shape change surfaces as a compile error instead of a runtime NaN.
type TillSummary = Exclude<
  NonNullable<ReceiptCombinedReport["sections"]["tills"]>[string],
  string
>;

export function buildPerTillSummaries(
  receipt: ReceiptCombinedReport,
  reports: SalesReport[],
  currency: Currency
): {
  tillName: string;
  productsSold: number;
  salesSummary: SalesSummaryData;
}[] {
  const tillsSection = receipt.sections.tills;
  if (!tillsSection) return [];

  const results: {
    tillName: string;
    productsSold: number;
    salesSummary: SalesSummaryData;
  }[] = [];

  Object.entries(tillsSection).forEach(([key, val]) => {
    if (typeof val === "string" || key === "sectionTitle") return;
    if (!("grossSales" in val)) return;

    const till: TillSummary = val;

    const extractCents = (values: { value: number }[]) =>
      Math.round((values[0]?.value ?? 0) * 100);

    // Units sold isn't in the combined receipt's per-till section, so sum it
    // from the raw SalesReports belonging to this till.
    const productsSold = reports
      .filter((r) => r.generated.tillName === till.label)
      .reduce(
        (sum, r) =>
          sum +
          Object.values(r.tillSession.sales).reduce(
            (s, c) => s + (c?.totalUnitSold ?? 0),
            0
          ),
        0
      );

    results.push({
      tillName: till.label,
      productsSold,
      salesSummary: {
        grossSales: extractCents(till.grossSales),
        discounts: extractCents(till.discounts),
        salesAfterDiscounts: extractCents(till.salesAfterDiscounts),
        refunds: extractCents(till.refunds),
        netSales: extractCents(till.netSales),
        currency
      }
    });
  });

  return results;
}

function buildCombinedCashDrawer(
  reports: SalesReport[],
  receipt: ReceiptReport
): Omit<CashDrawerData, "currency"> {
  const cashSalesCents = fieldToCents(receipt.sections.paymentsMethods.cash);
  const cashRefundsCents = sumCashRefunds(receipt);

  // Sum balances across every currency present, instead of guessing the
  // currency from the first sales entry.
  const sumBalance = (
    balance: SalesReport["tillSession"]["openCashBalance"] | undefined
  ) =>
    Object.values(balance ?? {}).reduce((sum: number, v) => sum + (v ?? 0), 0);

  let openingFloat = 0;
  let actualCash = 0;
  let expectedCash = 0;
  let manualOpenings = 0;
  let automaticOpenings = 0;

  for (const report of reports) {
    openingFloat += sumBalance(report.tillSession.openCashBalance);
    actualCash += sumBalance(report.tillSession.closeCashBalance);
    expectedCash += sumBalance(report.tillSession.expectedCashBalance);
    // Backward compatibility: old Z-reports only have `cashDrawerOpenings` (single total).
    // If the new split fields are both 0, fall back to the legacy field.
    const manual = report.tillSession.manualCashDrawerOpenings ?? 0;
    const automatic = report.tillSession.automaticCashDrawerOpenings ?? 0;
    const hasNewFields = manual > 0 || automatic > 0;

    manualOpenings += manual;
    automaticOpenings += hasNewFields
      ? automatic
      : report.tillSession.cashDrawerOpenings ?? 0;
  }

  const difference = actualCash - expectedCash;

  return {
    openingFloat,
    cashSales: cashSalesCents,
    cashRefunds: cashRefundsCents,
    expectedCash,
    actualCash,
    overShort: difference === 0 ? "balanced" : difference,
    manualOpenings,
    automaticOpenings
  };
}

export function useReportCombinedSections(
  reports: SalesReport[],
  receipt: ReceiptCombinedReport,
  fallbackCurrency?: Currency
): {
  combined: ReportSectionsData;
  perTill: {
    tillName: string;
    productsSold: number;
    salesSummary: SalesSummaryData;
  }[];
} {
  return useMemo(() => {
    const currency = getFirstCurrency(receipt, fallbackCurrency);

    const combined = assembleReportSections({
      receipt,
      salesSummary: buildSalesSummary(receipt, currency),
      cashDrawer: buildCombinedCashDrawer(reports, receipt),
      currency
    });

    const perTill = buildPerTillSummaries(receipt, reports, currency);

    return { combined, perTill };
  }, [reports, receipt, fallbackCurrency]);
}
